/**
 * AppointmentDetailPanel.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Panel de detalhes de agendamento — redesign visual conforme mockup aprovado.
 * Visual: clean, operacional, premium. Sem caixas pesadas, sem cores fortes.
 *
 * @module AppointmentDetailPanel
 */

import React, { useCallback, useState, useRef, useEffect } from 'react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import AppointmensState from '../../../../../consts/AppointmentsState';
import { getInitials, formatPrice, procedureLabels, appointmentProcedureIds } from '../../utils/appointmentUtils';
import ProcedureMultiSelect from '../ProcedureMultiSelect/ProcedureMultiSelect';
import { linesFromAppointment, linesToApi, linesTotals, insuranceTotals } from '../ProcedureMultiSelect/procedureLines';
import InsuranceProcedureLines from '../InsuranceProcedureLines/InsuranceProcedureLines';
import { parseAppointmentDate } from '../../utils/calendarUtils';
import { copyToClipboard } from '../../utils/copyUtils';
import {
  ATTENDANCE_IN_PERSON, ATTENDANCE_ONLINE, ATTENDANCE_MODE_OPTIONS,
  attendanceModeLabel, isValidMeetingUrl, MEETING_URL_ERROR,
} from '../../utils/attendanceMode';
import {
  BILLING_PRIVATE, BILLING_INSURANCE, BILLING_TYPE_OPTIONS, INSURANCE_CARD_REQUIRED_ERROR,
  coverageLabel, isInsuranceAppointment, normalizeBillingType,
} from '../../utils/billingType';
import CoverageEditDialog from './CoverageEditDialog';
import AppointmentInsuranceDocuments from './AppointmentInsuranceDocuments';
import InsuranceAlertList from '../InsuranceAlerts/InsuranceAlertList';
import AskPersonButton from '../InsuranceAlerts/AskPersonButton';
import { insuranceAlertsOf } from '../../utils/insuranceAlerts';
import { GetAppointmentFullInfo } from '../../../../../api/API';
import { WEEKDAYS_LONG } from '../../constants/calendarConstants';
import { SendWhatsAppMessage } from '../../../../../api/WhatsappAPI';
import MessagesTemplate from '../../../../../consts/MessagesTemplate';
import { sendAppointmentCreatedWhatsApp } from '../../../../../utils/appointmentWhatsAppUtils';
import { useCanSendAgendaWhatsApp } from '../../hooks/useAgendaWhatsAppSend';
import StatusActionBar from './StatusActionBar';
import PaymentSection from './PaymentSection';
import InlineEditSelector from './InlineEditSelector';
import AppointmentHistorySection from './AppointmentHistorySection';
import AppointmentStepsPill from './AppointmentStepsPill';
import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog';
import Toast from '../../../../../components/support/Toast';
import { describeConflict, uniqueConflicts } from '../AppointmentDrawer/useAppointmentConflicts';
import { TrashIcon } from './PaymentSectionIcons';
import DemoTag from '../../../../../components/demo-data/DemoTag';
import { isDemoRecord } from '../../../../../components/demo-data/demoData';
import { isWithinResourceHours } from '../../../../../utils/resourceSchedule';
import styles from './AppointmentDetailPanel.module.css';

const SAVE_ERROR = 'Não foi possível salvar a alteração.';

// ── Ícones SVG inline ─────────────────────────────────────────────────────────

const ProfileIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const DollarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="16"/>
    <path d="M9.5 10.5a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5"/>
  </svg>
);

const StethoscopeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3"/>
    <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4"/>
    <circle cx="20" cy="10" r="2"/>
  </svg>
);

const UserIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const RoomIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const VideoIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7"/>
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
  </svg>
);

const PinIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const LinkIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
  </svg>
);

const OpenIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const CopyIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const InsuranceCardIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2"/>
    <circle cx="8" cy="11" r="2"/>
    <path d="M5 16c.6-1.3 1.7-2 3-2s2.4.7 3 2"/>
    <line x1="14" y1="10" x2="19" y2="10"/>
    <line x1="14" y1="14" x2="18" y2="14"/>
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatAppointmentDateParts(dateStr) {
  if (!dateStr) return null;
  const date = parseAppointmentDate(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const weekday = WEEKDAYS_LONG[date.getDay()];
  return { day, month, year: String(year), weekday };
}

function formatStartTime(dateStr) {
  if (!dateStr) return '';
  const start = parseAppointmentDate(dateStr);
  const h = String(start.getHours()).padStart(2, '0');
  const m = String(start.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function formatEndTime(dateStr, duration) {
  if (!dateStr) return '';
  const start = parseAppointmentDate(dateStr);
  const durationMins = Number(duration) || 0;
  const end = new Date(start.getTime() + durationMins * 60 * 1000);
  const h = String(end.getHours()).padStart(2, '0');
  const m = String(end.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function AppointmentDetailPanel({
  isOpen,
  appointment,
  onClose,
  onStatusChange,
  onReschedule,
  onDelete,
  onCreateInvoice,
  onViewInvoice,
  onInvoiceCreated,
  onUpdate,
  onStepChecksChange,
  onNotifyPatient,
}) {
  // onCreateInvoice e onViewInvoice mantidos na assinatura para compatibilidade
  // com chamadores existentes. onCreateInvoice é repassado ao PaymentSection
  // para abrir a popup de emissão de NFS-e.
  // onInvoiceCreated: callback chamado após criar/editar fatura — atualiza calendário e drawer.
  // onUpdate: callback chamado após edição pontual de campo — atualiza o drawer com dados novos.
  // onStepChecksChange(id, stepChecks): etapas marcadas; a página corrige o card sem recarregar a agenda.
  // onNotifyPatient: aviso pelo sistema (useAgendaWhatsAppSend da página), usado pelo
  // cancelamento com aviso da StatusActionBar.
  const { CompanyConfig, GetPatientById, UpdateAppointment, DeleteAppointment } = DataProviderInstance();

  // ── Estado local do agendamento (para atualização imediata da UI após salvar) ──
  const [localAppointment, setLocalAppointment] = useState(appointment);

  // Sincroniza estado local quando a prop appointment mudar (ex: pai recarrega dados)
  useEffect(() => {
    setLocalAppointment(appointment);
  }, [appointment]);

  // Avisos do convênio (Fase 3) dependem dos documentos e da carteirinha:
  // depois de vincular, desvincular ou trocar a cobertura, busca de novo e avisa o pai
  const localAppointmentRef = useRef(localAppointment);
  localAppointmentRef.current = localAppointment;
  const refreshInsuranceAlerts = useCallback(() => {
    const id = localAppointmentRef.current?.id;
    if (!id) return;
    GetAppointmentFullInfo(id, (response) => {
      const alerts = response?.appointment?.insurance_alerts;
      if (!Array.isArray(alerts)) return;
      const next = { ...localAppointmentRef.current, insurance_alerts: alerts };
      setLocalAppointment(next);
      if (onUpdate) onUpdate(next);
    });
  }, [onUpdate]);

  // ── Estado de edição pontual ───────────────────────────────────────────────
  const [editingField, setEditingField] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  // Troca de agenda, sala ou procedimentos que choca com outro horário: { field, option, conflicts }
  const [conflictPrompt, setConflictPrompt] = useState(null);
  // Link da videochamada (modalidade online): rascunho da edição inline e feedback do copiar
  const [meetingUrlDraft, setMeetingUrlDraft] = useState('');
  const [meetingUrlError, setMeetingUrlError] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  // Linhas em edição no dialog de procedimentos ({ procedure_id, minutes, price })
  const [procedureDraft, setProcedureDraft] = useState([]);
  // Cobertura: erro devolvido pelo backend ao trocar para convênio (carteirinha recusada)
  const [coverageError, setCoverageError] = useState(null);
  // Alteração recusada pelo servidor (fora os casos com aviso próprio): o valor antigo fica
  const [saveError, setSaveError] = useState(null);

  // ── Excluir agendamento (ação secundária no rodapé, com confirmação) ──────
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteConfirmed = useCallback(() => {
    if (!appointment?.id) return;
    setShowDeleteConfirm(false);
    setIsDeleting(true);
    DeleteAppointment(appointment.id, () => {
      setIsDeleting(false);
      onDelete?.(appointment.id);
    });
  }, [appointment, DeleteAppointment, onDelete]);

  // ── Refs para posicionamento dos popovers ──────────────────────────────────
  const professionalRowRef = useRef(null);
  const roomRowRef = useRef(null);
  const modeRowRef = useRef(null);
  const coverageRowRef = useRef(null);

  const handleOpenProfile = useCallback(() => {
    const patientObj = appointment?.patient ? GetPatientById(appointment.patient) : null;
    const patientId = patientObj?.id;
    if (patientId) window.open(`/patients/${patientId}`, '_blank');
  }, [appointment, GetPatientById]);

  // Ícone de WhatsApp do cabeçalho. Com número próprio conectado mantém o
  // comportamento de sempre (abre a conversa, sem texto). Sem número próprio
  // abre o WhatsApp Web já com os dados do agendamento, a mesma mensagem que o
  // switch "Enviar dados do agendamento por WhatsApp" monta (achado 7 do QA 20261023-01).
  const canSendViaSystem = useCanSendAgendaWhatsApp();

  const handleSendWhatsApp = useCallback(() => {
    const patientObj = localAppointment?.patient ? GetPatientById(localAppointment.patient) : null;
    const phone = patientObj?.phone;
    if (!phone) return;
    const cc = patientObj?.countryCode || '';

    if (canSendViaSystem) {
      SendWhatsAppMessage(phone, MessagesTemplate.ConvertToWhatsAppText(''), cc);
      return;
    }

    let aptDate = null;
    try {
      aptDate = localAppointment?.date ? parseAppointmentDate(localAppointment.date) : null;
    } catch (_) { /* data inválida: cai no envio sem texto abaixo */ }
    if (!aptDate || Number.isNaN(aptDate.getTime())) {
      SendWhatsAppMessage(phone, MessagesTemplate.ConvertToWhatsAppText(''), cc);
      return;
    }

    const resource = (CompanyConfig?.Resources || []).find(
      r => String(r.Id) === String(localAppointment.resource)
    );
    const h = String(aptDate.getHours()).padStart(2, '0');
    const m = String(aptDate.getMinutes()).padStart(2, '0');
    sendAppointmentCreatedWhatsApp({
      patientPhone: phone,
      patientName: patientObj?.name || patientObj?.Name || '',
      countryCode: String(cc),
      aptDate,
      time: `${h}:${m}`,
      doctorName: resource?.Label || '',
      price: localAppointment?.price,
      companyConfig: CompanyConfig,
    });
  }, [localAppointment, GetPatientById, canSendViaSystem, CompanyConfig]);

  // ── Monta lista de opções para cada campo ──────────────────────────────────
  const getOptions = (field) => {
    if (field === 'professional') {
      return (CompanyConfig?.Resources || []).map(r => ({ id: r.Id, label: r.Label }));
    }
    if (field === 'room') {
      const roomOptions = (CompanyConfig?.Rooms || []).map(r => ({ id: r.Id, label: r.Label }));
      // Opção para remover a sala do agendamento
      return [{ id: null, label: 'Não definido' }, ...roomOptions];
    }
    if (field === 'attendance_mode') {
      return ATTENDANCE_MODE_OPTIONS;
    }
    if (field === 'billing_type') {
      return BILLING_TYPE_OPTIONS;
    }
    return [];
  };

  // ── Salva a edição pontual via API ─────────────────────────────────────────
  const handleInlineEdit = (field, selectedOption, force = false) => {
    if (isSaving) return;
    setIsSaving(true);

    const updatedAppointment = { ...localAppointment };

    if (field === 'procedures') {
      // selectedOption = linhas do dialog; duração e valor viram a soma delas
      const apiLines = linesToApi(selectedOption);
      const totals = linesTotals(selectedOption);
      updatedAppointment.procedures = apiLines;
      updatedAppointment.procedure = apiLines[0] ? String(apiLines[0].procedure_id) : updatedAppointment.procedure;
      updatedAppointment.duration = totals.minutes;
      updatedAppointment.price = totals.price;
    } else if (field === 'professional') {
      updatedAppointment.resource = selectedOption.id;
    } else if (field === 'room') {
      updatedAppointment.roomId = selectedOption.id;
    } else if (field === 'attendance_mode') {
      // Online solta a sala; presencial esquece o link (o backend faz o mesmo)
      updatedAppointment.attendance_mode = selectedOption.id;
      if (selectedOption.id === ATTENDANCE_ONLINE) updatedAppointment.roomId = null;
      else updatedAppointment.meeting_url = null;
    } else if (field === 'meeting_url') {
      updatedAppointment.meeting_url = selectedOption.id || null;
    } else if (field === 'billing_type') {
      // Particular solta a carteirinha (o backend faz o mesmo)
      updatedAppointment.billing_type = BILLING_PRIVATE;
      updatedAppointment.patient_insurance_id = null;
      updatedAppointment.insurance_provider_id = null;
      updatedAppointment.insurance_label = null;
      updatedAppointment.insurance_card_number = null;
    } else if (field === 'insurance_card') {
      // selectedOption = carteirinha escolhida no CoverageEditDialog
      const card = selectedOption;
      updatedAppointment.billing_type = BILLING_INSURANCE;
      updatedAppointment.patient_insurance_id = card.id;
      updatedAppointment.insurance_provider_id = card.provider_id != null ? String(card.provider_id) : null;
      updatedAppointment.insurance_label = [card.provider_name, card.plan_name].filter(Boolean).join(' · ') || null;
      updatedAppointment.insurance_card_number = card.card_number || null;
    }

    // normalizeAppointment() converte duration de "HH:MM:SS" → number para uso interno.
    // A API UpdateAppointment espera duration no formato "HH:MM:SS".
    // Prepara o payload para a API convertendo duration para "HH:MM:SS" se necessário,
    // mas mantém o updatedAppointment com duration como number para uso interno na UI.
    const rawDuration = updatedAppointment.duration;
    let apiDuration = rawDuration;
    if (typeof rawDuration === 'number' || (typeof rawDuration === 'string' && !String(rawDuration).includes(':'))) {
      const durationNum = Number(rawDuration) || 0;
      const durationHours = Math.floor(durationNum / 60);
      const durationMins = durationNum % 60;
      apiDuration = `${String(durationHours).padStart(2, '0')}:${String(durationMins).padStart(2, '0')}:00`;
    }
    // Agenda, sala e duração podem chocar com outro horário: o servidor confere e
    // devolve CONFLICT sem gravar, como no formulário do agendamento
    const checksConflicts = ['professional', 'room', 'procedures'].includes(field);
    const apiPayload = {
      ...updatedAppointment,
      duration: apiDuration,
      checkConflicts: checksConflicts,
      conflictConfirmed: checksConflicts && force,
    };

    UpdateAppointment(apiPayload, (response) => {
      setIsSaving(false);
      if (response?.status === 'CONFLICT') {
        setConflictPrompt({
          field,
          option: selectedOption,
          conflicts: Array.isArray(response.conflicts) ? response.conflicts : [],
        });
        return;
      }
      // O backend recusa link que não seja https: a edição fica aberta com o aviso
      if (field === 'meeting_url' && response && response.status && response.status !== 'SUCCESS') {
        setMeetingUrlError(MEETING_URL_ERROR);
        return;
      }
      // Carteirinha recusada (inativa, de outra pessoa): o diálogo fica aberto com o aviso
      if (field === 'insurance_card' && response && response.status && response.status !== 'SUCCESS') {
        setCoverageError(response.error || INSURANCE_CARD_REQUIRED_ERROR);
        return;
      }
      if (!response || response.status !== 'SUCCESS') {
        setEditingField(null);
        const reason = response?.message || response?.error;
        setSaveError(reason ? `${SAVE_ERROR} ${reason}.` : SAVE_ERROR);
        return;
      }
      setEditingField(null);
      // Atualiza estado local com duration como number (formato interno da UI)
      setLocalAppointment(updatedAppointment);
      // Notifica o pai para sincronizar (ex: atualizar drawerAppointment e calendário)
      if (onUpdate) onUpdate(updatedAppointment);
      if (field === 'billing_type' || field === 'insurance_card') refreshInsuranceAlerts();
    });
  };

  const handleCoverageSelect = (option) => {
    const current = normalizeBillingType(localAppointment?.billing_type);
    if (option.id === BILLING_INSURANCE) {
      setCoverageError(null);
      setEditingField('insurance_card');
      return;
    }
    if (current === BILLING_PRIVATE) {
      setEditingField(null);
      return;
    }
    handleInlineEdit('billing_type', option);
  };

  // Ao voltar para a aba (ex.: guia alterada na ficha em outra aba), reconfere em silêncio
  // os avisos e os documentos vinculados. Intervalo mínimo evita rajada de pedidos.
  const [insuranceRefreshToken, setInsuranceRefreshToken] = useState(0);
  const lastInsuranceRefreshRef = useRef(0);
  // Aviso "falta vincular" abre o Vincular documento do bloco abaixo
  const [insurancePickerToken, setInsurancePickerToken] = useState(0);
  const watchInsuranceRefocus = !!isOpen && !!localAppointment?.id
    && localAppointment.status !== AppointmensState.Blocked
    && isInsuranceAppointment(localAppointment);
  useEffect(() => {
    if (!watchInsuranceRefocus) return undefined;
    const handleRefocus = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastInsuranceRefreshRef.current < 2000) return;
      lastInsuranceRefreshRef.current = now;
      refreshInsuranceAlerts();
      setInsuranceRefreshToken((token) => token + 1);
    };
    window.addEventListener('focus', handleRefocus);
    document.addEventListener('visibilitychange', handleRefocus);
    return () => {
      window.removeEventListener('focus', handleRefocus);
      document.removeEventListener('visibilitychange', handleRefocus);
    };
  }, [watchInsuranceRefocus, refreshInsuranceAlerts]);

  if (!isOpen || !appointment) return null;

  // ── Resolve nomes (usa localAppointment para refletir edições pontuais) ────

  const patientObj = localAppointment.patient ? GetPatientById(localAppointment.patient) : null;
  const patientName = patientObj?.name || patientObj?.Name || String(localAppointment.patient || '');

  const resourceObj = (CompanyConfig?.Resources || []).find(
    r => String(r.Id) === String(localAppointment.resource)
  );
  const resourceLabel = resourceObj?.Label || localAppointment.resource || '';

  // Todos os procedimentos do agendamento ("Axila + Virilha + Pernas")
  const procedureCount = appointmentProcedureIds(localAppointment).length;
  const procedureLabel = procedureLabels(localAppointment, CompanyConfig?.Procedures)
    || localAppointment.procedure || '';
  const openProcedureEdit = () => {
    setProcedureDraft(linesFromAppointment(localAppointment, CompanyConfig?.Procedures));
    setEditingField('procedures');
  };

  const roomObj = (CompanyConfig?.Rooms || []).find(
    r => String(r.Id) === String(localAppointment.roomId)
  );
  const roomLabel = roomObj?.Label || localAppointment.roomName || null;

  // ── Modalidade e link da videochamada ──────────────────────────────────────
  const isOnline = localAppointment.attendance_mode === ATTENDANCE_ONLINE;
  const meetingUrl = isOnline ? (localAppointment.meeting_url || null) : null;
  const openMeetingUrlEdit = (e) => {
    e?.stopPropagation();
    setMeetingUrlDraft(meetingUrl || '');
    setMeetingUrlError(null);
    setEditingField('meeting_url');
  };
  const cancelMeetingUrlEdit = () => {
    setEditingField(null);
    setMeetingUrlError(null);
  };
  const submitMeetingUrl = (e) => {
    e.preventDefault();
    const value = meetingUrlDraft.trim();
    if (!isValidMeetingUrl(value)) {
      setMeetingUrlError(MEETING_URL_ERROR);
      return;
    }
    handleInlineEdit('meeting_url', { id: value || null });
  };
  const openMeeting = (e) => {
    e.stopPropagation();
    if (meetingUrl) window.open(meetingUrl, '_blank', 'noopener,noreferrer');
  };
  const copyMeetingLink = async (e) => {
    e.stopPropagation();
    if (!meetingUrl) return;
    const result = await copyToClipboard(meetingUrl);
    if (result?.success) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const initials = getInitials(patientName);
  const subtitle = [procedureLabel, resourceLabel].filter(Boolean).join(' • ');

  const dateParts = formatAppointmentDateParts(localAppointment.date);
  const startTime = formatStartTime(localAppointment.date);
  const endTime = formatEndTime(localAppointment.date, localAppointment.duration);
  const durationMins = Number(localAppointment.duration) || 0;
  const priceLabel = localAppointment.price ? formatPrice(localAppointment.price) : null;

  const hasInvoice = localAppointment.invoiceId && localAppointment.invoiceId !== '0' && localAppointment.invoiceId !== 0;
  const isBlocked = localAppointment.status === AppointmensState.Blocked;

  // Agendamento salvo fora do horário próprio do profissional: só avisa
  const isOffProfessionalHours = !isBlocked && !!localAppointment.date && isWithinResourceHours(
    resourceObj?.Schedule,
    parseAppointmentDate(localAppointment.date),
    startTime,
    durationMins,
  ) === false;

  const deleteDateLabel = dateParts && startTime
    ? `${dateParts.day}/${dateParts.month}/${dateParts.year} às ${startTime}`
    : null;
  const deleteDialogMessage = (
    <span>
      Deseja realmente excluir este agendamento? Esta ação não pode ser desfeita.
      {(patientName || deleteDateLabel) && (
        <span style={{ display: 'block', marginTop: 8, fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>
          {patientName     && <span style={{ display: 'block' }}>Paciente: <strong>{patientName}</strong></span>}
          {deleteDateLabel && <span style={{ display: 'block' }}>Data: <strong>{deleteDateLabel}</strong></span>}
        </span>
      )}
    </span>
  );

  // ── Escopo do bloqueio ─────────────────────────────────────────────────────
  const isGeneralBlock = isBlocked && (!localAppointment.resource || localAppointment.resource === '');
  const blockResourceObj = isBlocked && localAppointment.resource
    ? (CompanyConfig?.Resources || []).find(r => String(r.Id) === String(localAppointment.resource))
    : null;
  const blockResourceLabel = blockResourceObj?.Label || null;

  // ── Verifica se há salas cadastradas para exibir a linha de sala ───────────
  const hasRooms = (CompanyConfig?.Rooms || []).length > 0;

  // ── Cobertura: só para quem trabalha com convênio (ou agendamento já por convênio) ──
  const isInsurance = isInsuranceAppointment(localAppointment);
  const insuranceProviders = CompanyConfig?.InsuranceProviders || [];
  const showCoverage = !isBlocked && (insuranceProviders.length > 0 || isInsurance);
  // Valor da tabela da operadora (soma das linhas com código escolhido)
  const operatorTotals = isInsurance ? insuranceTotals(localAppointment.procedures || []) : { total: 0, count: 0 };

  return (
    <div className={styles.panelContainer} role="dialog" aria-modal="true" aria-label="Detalhes do agendamento">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className={styles.panelHeader}>
        <div className={styles.patientAvatar} aria-hidden="true">
          {initials}
        </div>

        <div className={styles.patientInfo}>
          <div className={styles.patientNameRow}>
            <h2 className={styles.patientName}>{patientName}</h2>
            {isDemoRecord(localAppointment) && <DemoTag />}
            <div className={styles.patientQuickActions}>
              {patientObj && (
                <button
                  type="button"
                  className={styles.patientActionBtn}
                  onClick={handleOpenProfile}
                  title="Ver perfil do paciente"
                >
                  <ProfileIcon />
                </button>
              )}
              {patientObj?.phone && (
                <button
                  type="button"
                  className={`${styles.patientActionBtn} ${styles.patientActionBtnWhatsapp}`}
                  onClick={handleSendWhatsApp}
                  title={canSendViaSystem
                    ? 'Enviar mensagem no WhatsApp'
                    : 'Abrir no WhatsApp Web com a mensagem pronta'}
                >
                  <WhatsAppIcon />
                </button>
              )}
            </div>
          </div>
          {subtitle && (
            <span className={styles.patientSubtitle}>{subtitle}</span>
          )}
        </div>

        <button
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Fechar"
          title="Fechar"
          type="button"
        >
          <XIcon />
        </button>
      </div>

      {/* ── Corpo ───────────────────────────────────────────────────────────── */}
      <div className={styles.panelBody}>

        {/* ── Cards de métricas: Data | Horário | Valor ────────────────────── */}
        {!isBlocked && (
          <div className={styles.metricsRow}>

            {dateParts && (
              <div className={styles.metricCard}>
                <span className={styles.metricIcon}><CalendarIcon /></span>
                <span className={styles.metricLabel}>DATA</span>
                <div className={styles.metricValue}>
                  <span className={styles.metricValueMain}>{dateParts.day}/{dateParts.month}</span>
                  <span className={styles.metricValueYear}>/{dateParts.year}</span>
                </div>
                <span className={styles.metricSub}>{dateParts.weekday}</span>
              </div>
            )}

            {startTime && (
              <div className={styles.metricCard}>
                <span className={styles.metricIcon}><ClockIcon /></span>
                <span className={styles.metricLabel}>HORÁRIO</span>
                <div className={styles.metricValue}>
                  <span className={styles.metricValueMain}>{startTime}</span>
                </div>
                <span className={styles.metricSub}>
                  {endTime && startTime !== endTime ? `até ${endTime}` : ''}
                  {durationMins > 0 ? ` (${durationMins} min)` : ''}
                </span>
              </div>
            )}

            {(priceLabel || isInsurance) && (
              <div className={styles.metricCard}>
                <span className={styles.metricIcon}><DollarIcon /></span>
                <span className={styles.metricLabel}>VALOR</span>
                <div className={styles.metricValue}>
                  {/* Por convênio quem paga é a operadora: com tabela, o valor dela; sem, o preço fica só como referência */}
                  <span className={`${styles.metricValueMain} ${styles.metricValuePrice}`}>
                    {!isInsurance ? priceLabel : (operatorTotals.count > 0 ? formatPrice(operatorTotals.total) : 'Convênio')}
                  </span>
                </div>
                <span className={styles.metricSub}>
                  {isInsurance && operatorTotals.count > 0
                    ? 'pela operadora'
                    : (isInsurance && Number(localAppointment.price) > 0 ? `ref. ${priceLabel}` : <>&nbsp;</>)}
                </span>
              </div>
            )}

          </div>
        )}

        {/* ── Lista: Procedimento / Profissional / Modalidade / Sala ou Link ── */}
        {(procedureLabel || resourceLabel || roomLabel || hasRooms || !isBlocked) && (
          <div className={styles.detailList}>

            {procedureLabel && (
              <div
                className={`${styles.detailRow} ${styles.editableRow}`}
                onClick={openProcedureEdit}
                title="Clique para alterar os procedimentos"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && openProcedureEdit()}
              >
                <span className={styles.detailRowIcon}><StethoscopeIcon /></span>
                <div className={styles.detailRowContent}>
                  <span className={styles.detailRowLabel}>
                    {procedureCount > 1 ? 'Procedimentos' : 'Procedimento'}
                  </span>
                  <span className={styles.detailRowValue} title={procedureLabel}>{procedureLabel}</span>
                </div>
                <span className={styles.editIcon}><PencilIcon /></span>
              </div>
            )}

            {resourceLabel && (
              <div
                ref={professionalRowRef}
                className={`${styles.detailRow} ${styles.editableRow}`}
                onClick={() => setEditingField('professional')}
                title="Clique para alterar a agenda"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setEditingField('professional')}
              >
                <span className={styles.detailRowIcon}><UserIcon /></span>
                <div className={styles.detailRowContent}>
                  <span className={styles.detailRowLabel}>Agenda</span>
                  <span className={styles.detailRowValue}>{resourceLabel}</span>
                  {isOffProfessionalHours && (
                    <span className={styles.detailRowWarning}>Fora do horário deste profissional</span>
                  )}
                </div>
                <span className={styles.editIcon}><PencilIcon /></span>
              </div>
            )}

            {!isBlocked && (
              <div
                ref={modeRowRef}
                className={`${styles.detailRow} ${styles.editableRow}`}
                onClick={() => setEditingField('attendance_mode')}
                title="Clique para alterar a modalidade"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setEditingField('attendance_mode')}
              >
                <span className={styles.detailRowIcon}>{isOnline ? <VideoIcon /> : <PinIcon />}</span>
                <div className={styles.detailRowContent}>
                  <span className={styles.detailRowLabel}>Modalidade</span>
                  <span className={styles.detailRowValue}>{attendanceModeLabel(localAppointment.attendance_mode)}</span>
                </div>
                <span className={styles.editIcon}><PencilIcon /></span>
              </div>
            )}

            {showCoverage && (
              <div
                ref={coverageRowRef}
                className={`${styles.detailRow} ${styles.editableRow}`}
                onClick={() => setEditingField('billing_type')}
                title="Clique para alterar a cobertura"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setEditingField('billing_type')}
              >
                <span className={styles.detailRowIcon}><InsuranceCardIcon /></span>
                <div className={styles.detailRowContent}>
                  <span className={styles.detailRowLabel}>Cobertura</span>
                  <span className={styles.detailRowValue} title={coverageLabel(localAppointment)}>
                    {coverageLabel(localAppointment)}
                  </span>
                  {isInsurance && localAppointment.insurance_card_number && (
                    <span className={styles.detailRowSub}>Carteirinha {localAppointment.insurance_card_number}</span>
                  )}
                </div>
                <span className={styles.editIcon}><PencilIcon /></span>
              </div>
            )}

            {showCoverage && isInsurance && insuranceAlertsOf(localAppointment).length > 0 && (
              <div className={styles.insuranceAlerts}>
                <InsuranceAlertList
                  alerts={insuranceAlertsOf(localAppointment)}
                  onUnlinkedClick={() => setInsurancePickerToken(Date.now())}
                />
                <div className={styles.insuranceAsk}>
                  <AskPersonButton
                    alerts={insuranceAlertsOf(localAppointment)}
                    patientId={localAppointment.patient}
                    date={localAppointment.date}
                    providerName={(localAppointment.insurance_label || '').split(' · ')[0]}
                  />
                </div>
              </div>
            )}

            {isOnline && (
              <div
                className={`${styles.detailRow} ${editingField === 'meeting_url' ? '' : styles.editableRow}`}
                onClick={editingField === 'meeting_url' ? undefined : openMeetingUrlEdit}
                title={editingField === 'meeting_url' ? undefined : (meetingUrl ? 'Clique para alterar o link' : 'Clique para informar o link')}
                role={editingField === 'meeting_url' ? undefined : 'button'}
                tabIndex={editingField === 'meeting_url' ? undefined : 0}
                onKeyDown={(e) => editingField !== 'meeting_url' && e.key === 'Enter' && openMeetingUrlEdit()}
              >
                <span className={styles.detailRowIcon}><LinkIcon /></span>
                {editingField === 'meeting_url' ? (
                  <form className={styles.meetingLinkEditor} onSubmit={submitMeetingUrl} onClick={(e) => e.stopPropagation()}>
                    <span className={styles.detailRowLabel}>Link da videochamada</span>
                    <input
                      autoFocus
                      type="url"
                      inputMode="url"
                      autoComplete="off"
                      className={styles.meetingLinkInput}
                      placeholder="https://..."
                      value={meetingUrlDraft}
                      onChange={(e) => { setMeetingUrlDraft(e.target.value); setMeetingUrlError(null); }}
                      onKeyDown={(e) => e.key === 'Escape' && cancelMeetingUrlEdit()}
                    />
                    {meetingUrlError && <span className={styles.meetingLinkError}>{meetingUrlError}</span>}
                    <div className={styles.meetingLinkEditorActions}>
                      <button type="button" className={styles.meetingLinkGhostBtn} onClick={cancelMeetingUrlEdit}>Cancelar</button>
                      <button type="submit" className={styles.meetingLinkSaveBtn} disabled={isSaving}>Salvar</button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className={styles.detailRowContent}>
                      <span className={styles.detailRowLabel}>Link da videochamada</span>
                      {meetingUrl ? (
                        <span className={`${styles.detailRowValue} ${styles.detailRowValueLink}`} title={meetingUrl}>{meetingUrl}</span>
                      ) : (
                        <span className={styles.detailRowValueEmpty}>Link ainda não informado</span>
                      )}
                      {meetingUrl && (
                        <div className={styles.meetingLinkActions}>
                          <button type="button" className={`${styles.meetingLinkAction} ${styles.meetingLinkActionPrimary}`} onClick={openMeeting} title="Abre em uma nova aba">
                            <OpenIcon /> Abrir videochamada
                          </button>
                          <button type="button" className={styles.meetingLinkAction} onClick={copyMeetingLink} title="Copiar o link para compartilhar">
                            <CopyIcon /> {linkCopied ? 'Copiado' : 'Copiar link'}
                          </button>
                        </div>
                      )}
                    </div>
                    <span className={styles.editIcon}><PencilIcon /></span>
                  </>
                )}
              </div>
            )}

            {!isOnline && (roomLabel || hasRooms) && (
              <div
                ref={roomRowRef}
                className={`${styles.detailRow} ${styles.editableRow}`}
                onClick={() => setEditingField('room')}
                title="Clique para alterar a sala"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setEditingField('room')}
              >
                <span className={styles.detailRowIcon}><RoomIcon /></span>
                <div className={styles.detailRowContent}>
                  <span className={styles.detailRowLabel}>Sala</span>
                  <span className={styles.detailRowValue}>{roomLabel || <span className={styles.detailRowValueEmpty}>Não definida</span>}</span>
                </div>
                <span className={styles.editIcon}><PencilIcon /></span>
              </div>
            )}

          </div>
        )}

        {/* ── Escopo do bloqueio — exibido apenas para agendamentos bloqueados ── */}
        {isBlocked && (
          <div className={styles.blockScopeInfo}>
            <span className={styles.blockScopeIcon}><LockOutlinedIcon fontSize="inherit" /></span>
            <div className={styles.blockScopeText}>
              {isGeneralBlock ? (
                <>
                  <span className={styles.blockScopeTitle}>Bloqueio geral da empresa</span>
                  <span className={styles.blockScopeDesc}>Este horário está bloqueado em todas as agendas</span>
                </>
              ) : (
                <>
                  <span className={styles.blockScopeTitle}>Bloqueio de agenda</span>
                  <span className={styles.blockScopeDesc}>
                    {blockResourceLabel
                      ? `Bloqueado apenas para: ${blockResourceLabel}`
                      : 'Bloqueado em uma agenda específica'}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Barra de ações (status + ações rápidas) ──────────────────────── */}
        <StatusActionBar
          appointment={appointment}
          onStatusChange={onStatusChange}
          onReschedule={onReschedule}
          onDelete={onDelete}
          onCreateInvoice={onCreateInvoice}
          onViewInvoice={onViewInvoice}
          onNotifyPatient={onNotifyPatient}
        >
          {/* Etapas do status atual (checklist da empresa); some quando não há etapas */}
          {!isBlocked && (
            <AppointmentStepsPill
              appointment={localAppointment}
              onChecksChange={(stepChecks) => {
                setLocalAppointment((current) => ({ ...current, stepChecks }));
                if (onStepChecksChange) onStepChecksChange(appointment.id, stepChecks);
              }}
            />
          )}
        </StatusActionBar>

        {/* ── Seção de pagamento ───────────────────────────────────────────── */}
        {!isBlocked && (
          <PaymentSection
            appointment={appointment}
            isInsurance={isInsurance}
            onCreateInvoice={onCreateInvoice}
            onInvoiceCreated={onInvoiceCreated}
          />
        )}

        {/* ── Documentos do convênio (pedidos e guias) — só em convênio ───── */}
        {!isBlocked && isInsurance && localAppointment.id && (
          <AppointmentInsuranceDocuments
            appointment={localAppointment}
            onChanged={refreshInsuranceAlerts}
            refreshToken={insuranceRefreshToken}
            openPickerToken={insurancePickerToken}
          />
        )}

        {/* ── Observações — só exibe se houver conteúdo ───────────────────── */}
        {appointment.notes && (
          <div className={styles.notesSection}>
            <div className={styles.notesLabel}>Observações</div>
            <p className={styles.notesText}>{appointment.notes}</p>
          </div>
        )}

        {/* ── Histórico (quem criou/alterou e quando) ─────────────────────── */}
        {localAppointment.id && <AppointmentHistorySection appointment={localAppointment} />}

        {/* ── Excluir agendamento — ação secundária, afastada do status ──── */}
        {appointment.id && !isBlocked && (
          <div className={styles.panelDeleteRow}>
            <button
              type="button"
              className={styles.panelDeleteBtn}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting || !!hasInvoice}
              title={hasInvoice ? 'Não é possível excluir um agendamento com pagamento vinculado' : 'Excluir agendamento'}
            >
              {isDeleting ? <span className={styles.spinner} /> : <TrashIcon />}
              <span>Excluir agendamento</span>
            </button>
          </div>
        )}

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        {appointment.id && (
          <div className={styles.panelFooter}>
            {appointment.createdAt && (
              <span>Criado em {appointment.createdAt}</span>
            )}
            <span className={styles.footerSpacer} />
            <span>ID #{appointment.id}</span>
          </div>
        )}

      </div>

      {/* ── Confirmação de exclusão ──────────────────────────────────────────── */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Excluir agendamento"
        message={deleteDialogMessage}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setShowDeleteConfirm(false)}
        loading={isDeleting}
      />

      <Toast
        open={Boolean(saveError)}
        onClose={() => setSaveError(null)}
        message={saveError || ''}
        severity="error"
        autoHideDuration={6000}
      />

      {/* ── Choque de horário: encaixar mesmo assim ─────────────────────────── */}
      <ConfirmDialog
        isOpen={Boolean(conflictPrompt)}
        variant="warning"
        title="Encaixar mesmo assim?"
        message="Esse horário já está ocupado:"
        confirmLabel="Encaixar mesmo assim"
        cancelLabel="Voltar"
        onConfirm={() => {
          const { field, option } = conflictPrompt;
          setConflictPrompt(null);
          handleInlineEdit(field, option, true);
        }}
        onCancel={() => setConflictPrompt(null)}
      >
        <ul className={styles.conflictList}>
          {uniqueConflicts(conflictPrompt?.conflicts).map(c => (
            <li key={c.id}>{describeConflict(c)}</li>
          ))}
        </ul>
      </ConfirmDialog>

      {/* ── Procedimentos: um ou mais, com duração e valor por linha ────────── */}
      <ConfirmDialog
        isOpen={editingField === 'procedures'}
        title="Procedimentos"
        variant="info"
        confirmLabel="Salvar"
        onConfirm={() => handleInlineEdit('procedures', procedureDraft)}
        onCancel={() => { if (!isSaving) setEditingField(null); }}
        confirmDisabled={procedureDraft.length === 0}
        loading={isSaving}
        maxWidth={460}
      >
        <ProcedureMultiSelect
          procedures={(CompanyConfig?.Procedures || []).filter(p => p.Active == null || Number(p.Active) === 1)}
          value={procedureDraft}
          onChange={setProcedureDraft}
          error={procedureDraft.length === 0 ? 'Selecione pelo menos um procedimento' : undefined}
          disabled={isSaving}
          inline
          showTotals
        />
        {isInsurance && localAppointment.insurance_provider_id && (
          <InsuranceProcedureLines
            providerId={localAppointment.insurance_provider_id}
            lines={procedureDraft}
            procedures={CompanyConfig?.Procedures || []}
            onChange={setProcedureDraft}
            disabled={isSaving}
          />
        )}
      </ConfirmDialog>

      {/* ── Seletores de edição pontual ──────────────────────────────────────── */}
      <InlineEditSelector
        isOpen={editingField === 'professional'}
        onClose={() => setEditingField(null)}
        title="Selecionar agenda"
        options={getOptions('professional')}
        selectedId={localAppointment?.resource}
        onSelect={(option) => handleInlineEdit('professional', option)}
        anchorEl={professionalRowRef.current}
      />
      <InlineEditSelector
        isOpen={editingField === 'room'}
        onClose={() => setEditingField(null)}
        title="Selecionar sala"
        options={getOptions('room')}
        selectedId={localAppointment?.roomId}
        onSelect={(option) => handleInlineEdit('room', option)}
        anchorEl={roomRowRef.current}
      />
      <InlineEditSelector
        isOpen={editingField === 'attendance_mode'}
        onClose={() => setEditingField(null)}
        title="Modalidade"
        options={getOptions('attendance_mode')}
        selectedId={localAppointment?.attendance_mode || ATTENDANCE_IN_PERSON}
        onSelect={(option) => handleInlineEdit('attendance_mode', option)}
        anchorEl={modeRowRef.current}
      />
      <InlineEditSelector
        isOpen={editingField === 'billing_type'}
        // O seletor chama onSelect e depois onClose: escolher Convênio já abriu o
        // diálogo da carteirinha, que não pode ser fechado aqui.
        onClose={() => setEditingField(f => (f === 'billing_type' ? null : f))}
        title="Cobertura"
        options={getOptions('billing_type')}
        selectedId={normalizeBillingType(localAppointment?.billing_type)}
        onSelect={handleCoverageSelect}
        anchorEl={coverageRowRef.current}
      />
      {showCoverage && (
        <CoverageEditDialog
          isOpen={editingField === 'insurance_card'}
          patientId={localAppointment?.patient}
          providers={insuranceProviders}
          currentCardId={isInsurance ? localAppointment?.patient_insurance_id : null}
          saving={isSaving}
          error={coverageError}
          onConfirm={(card) => handleInlineEdit('insurance_card', card)}
          onCancel={() => { setEditingField(null); setCoverageError(null); }}
        />
      )}
    </div>
  );
}
